/**
 * Collaborator Notification Types and Constants
 * Based on COLLABORATOR-NOTIFICATION-GUIDE.md
 * 
 * @description Defines all collaborator-related notification types including:
 * - Follow notifications
 * - Collaboration request notifications
 * - Rating notifications
 * - Endorsement notifications
 */

// ==========================================
// COLLABORATOR NOTIFICATION TYPES ENUM
// ==========================================
export const COLLABORATOR_NOTIFICATION_TYPES = {
  // Follow
  NEW_FOLLOWER: 'new_follower',

  // Collaboration Requests
  COLLABORATION_REQUEST_RECEIVED: 'collaboration_request_received',
  COLLABORATION_REQUEST_ACCEPTED: 'collaboration_request_accepted',
  COLLABORATION_REQUEST_REJECTED: 'collaboration_request_rejected',

  // Ratings
  RATING_RECEIVED: 'rating_received',
  RATING_RESPONSE_RECEIVED: 'rating_response_received', // Reserved for future use

  // Endorsements
  ENDORSEMENT_RECEIVED: 'endorsement_received',
  ENDORSEMENT_ACCEPTED: 'endorsement_accepted',
  ENDORSEMENT_DECLINED: 'endorsement_declined',
};

// ==========================================
// NOTIFICATION TYPE SET (O(1) lookup)
// ==========================================
export const COLLABORATOR_NOTIFICATION_TYPES_SET = new Set([
  COLLABORATOR_NOTIFICATION_TYPES.NEW_FOLLOWER,
  COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_RECEIVED,
  COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_ACCEPTED,
  COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_REJECTED,
  COLLABORATOR_NOTIFICATION_TYPES.RATING_RECEIVED,
  COLLABORATOR_NOTIFICATION_TYPES.RATING_RESPONSE_RECEIVED,
  COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_RECEIVED,
  COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_ACCEPTED,
  COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_DECLINED,
]);

// ==========================================
// NOTIFICATION TYPE ARRAY (for filtering)
// ==========================================
export const COLLABORATOR_NOTIFICATION_TYPES_ARRAY = [
  COLLABORATOR_NOTIFICATION_TYPES.NEW_FOLLOWER,
  COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_RECEIVED,
  COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_ACCEPTED,
  COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_REJECTED,
  COLLABORATOR_NOTIFICATION_TYPES.RATING_RECEIVED,
  COLLABORATOR_NOTIFICATION_TYPES.RATING_RESPONSE_RECEIVED,
  COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_RECEIVED,
  COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_ACCEPTED,
  COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_DECLINED,
];

// ==========================================
// NOTIFICATION ICONS (emoji-based for consistency)
// ==========================================
export const COLLABORATOR_NOTIFICATION_ICONS = {
  [COLLABORATOR_NOTIFICATION_TYPES.NEW_FOLLOWER]: '👤',
  [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_RECEIVED]: '🤝',
  [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_ACCEPTED]: '✅',
  [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_REJECTED]: '❌',
  [COLLABORATOR_NOTIFICATION_TYPES.RATING_RECEIVED]: '⭐',
  [COLLABORATOR_NOTIFICATION_TYPES.RATING_RESPONSE_RECEIVED]: '⭐',
  [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_RECEIVED]: '🏆',
  [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_ACCEPTED]: '👍',
  [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_DECLINED]: '👎',
};

// ==========================================
// NOTIFICATION COLORS (CSS class suffixes)
// ==========================================
export const COLLABORATOR_NOTIFICATION_COLORS = {
  [COLLABORATOR_NOTIFICATION_TYPES.NEW_FOLLOWER]: 'blue',
  [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_RECEIVED]: 'purple',
  [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_ACCEPTED]: 'green',
  [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_REJECTED]: 'gray',
  [COLLABORATOR_NOTIFICATION_TYPES.RATING_RECEIVED]: 'yellow',
  [COLLABORATOR_NOTIFICATION_TYPES.RATING_RESPONSE_RECEIVED]: 'yellow',
  [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_RECEIVED]: 'gold',
  [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_ACCEPTED]: 'green',
  [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_DECLINED]: 'gray',
};

// ==========================================
// NOTIFICATION PRIORITIES
// ==========================================
export const COLLABORATOR_NOTIFICATION_PRIORITIES = {
  [COLLABORATOR_NOTIFICATION_TYPES.NEW_FOLLOWER]: 'normal',
  [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_RECEIVED]: 'high',
  [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_ACCEPTED]: 'high',
  [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_REJECTED]: 'normal',
  [COLLABORATOR_NOTIFICATION_TYPES.RATING_RECEIVED]: 'normal', // becomes 'high' if rating >= 4
  [COLLABORATOR_NOTIFICATION_TYPES.RATING_RESPONSE_RECEIVED]: 'normal',
  [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_RECEIVED]: 'normal',
  [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_ACCEPTED]: 'low',
  [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_DECLINED]: 'low',
};

// ==========================================
// ACTION REQUIRED FLAGS
// ==========================================
export const COLLABORATOR_ACTION_REQUIRED = {
  [COLLABORATOR_NOTIFICATION_TYPES.NEW_FOLLOWER]: false,
  [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_RECEIVED]: true,
  [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_ACCEPTED]: false,
  [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_REJECTED]: false,
  [COLLABORATOR_NOTIFICATION_TYPES.RATING_RECEIVED]: false,
  [COLLABORATOR_NOTIFICATION_TYPES.RATING_RESPONSE_RECEIVED]: false,
  [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_RECEIVED]: true,
  [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_ACCEPTED]: false,
  [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_DECLINED]: false,
};

// ==========================================
// ENDORSEMENT LEVEL BADGES
// ==========================================
export const ENDORSEMENT_LEVEL_BADGES = {
  beginner: '🌱',
  intermediate: '📈',
  advanced: '🚀',
  expert: '🏆',
};

// ==========================================
// PRIORITY STYLES (similar to workshop implementation)
// ==========================================
export const COLLABORATOR_PRIORITY_STYLES = Object.freeze({
  high: Object.freeze({
    bgClass: 'priority-high',
    borderColor: '#8b5cf6', // Purple for collaboration
    iconColor: '#8b5cf6',
    showToast: true,
    toastDuration: 7000,
    playSound: true,
    toastType: 'warning',
  }),
  normal: Object.freeze({
    bgClass: 'priority-normal',
    borderColor: '#3b82f6',
    iconColor: '#3b82f6',
    showToast: true,
    toastDuration: 5000,
    playSound: false,
    toastType: 'info',
  }),
  low: Object.freeze({
    bgClass: 'priority-low',
    borderColor: '#9ca3af',
    iconColor: '#9ca3af',
    showToast: false,
    toastDuration: 3000,
    playSound: false,
    toastType: 'info',
  }),
});

// ==========================================
// HIGH PRIORITY TYPES (require prominent display)
// ==========================================
export const COLLABORATOR_HIGH_PRIORITY_TYPES = [
  COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_RECEIVED,
  COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_ACCEPTED,
];

// ==========================================
// TYPES REQUIRING USER ACTION
// ==========================================
export const COLLABORATOR_ACTIONABLE_TYPES = [
  COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_RECEIVED,
  COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_RECEIVED,
];

export default COLLABORATOR_NOTIFICATION_TYPES;
